import { createRoute, Navigate, Outlet, redirect } from "@tanstack/react-router";
import { SupplierBuyerRelationshipPageRoute } from "pages/app/SupplierBuyerRelationshipPage";
import { SupplierProfilePage } from "pages/app/SupplierProfilePage";
import { SupplierSearchPage } from "pages/app/SupplierSearchPage";

import { SupplierDetailLayout } from "components/supplier_details/SupplierDetailLayout";
import { prefetchSupplierLookup } from "lib/hooks/api/supplier/useSupplierDetails";
import { getCachedCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { rootRoute } from "../rootRoute";

// /suppliers layout — groups all supplier pages under one prefix
export const suppliersLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/suppliers",
  staticData: { pageName: "", breadcrumbLabel: "Suppliers" },
  component: Outlet,
});

// /suppliers/ → redirect to /suppliers/search
export const suppliersIndexRoute = createRoute({
  getParentRoute: () => suppliersLayoutRoute,
  path: "/",
  staticData: { pageName: "Find suppliers" },
  component: function SuppliersIndexRedirect() {
    return <Navigate to="/suppliers/search" replace />;
  },
});

// /suppliers/search — list page (Find suppliers). `pageName` deliberately empty so the
// router-level `tracking.pageView` doesn't fire for this route — the page emits its own
// "Supplier Search" event to preserve the existing analytics event name.
export const suppliersSearchRoute = createRoute({
  getParentRoute: () => suppliersLayoutRoute,
  path: "/search",
  staticData: { pageName: "" },
  component: SupplierSearchPage,
});

// /suppliers/$id layout — wraps all supplier detail sub-pages with shared data fetching.
// GUID is the canonical URL form: the loader resolves the supplier up-front and issues
// numeric→GUID and archived-replacement redirects before any child component renders.
export const supplierIdLayoutRoute = createRoute({
  getParentRoute: () => suppliersLayoutRoute,
  path: "/$id",
  staticData: { pageName: "" },
  loader: async ({ params: { id }, location, context: { queryClient, graphQLClient } }) => {
    // Bail before any GraphQL request when nobody is signed in: the context client is then
    // anonymous, and the supplier request would throw into the error boundary.
    if (!getCachedCurrentUser(queryClient)) return;

    const supplier = await prefetchSupplierLookup(queryClient, graphQLClient, id);
    if (!supplier) return;

    // Preserve the rest of the URL (tab suffix, search params) across the redirect.
    const suffix = location.pathname.slice(`/suppliers/${id}`.length);

    // Archived supplier with exactly one replacement → redirect straight to the replacement's
    // GUID (single hop). The guard against redirecting to the current id breaks self/cyclic
    // replacement chains.
    if (
      supplier.isArchived &&
      supplier.replacedBySuppliers?.length === 1 &&
      supplier.replacedBySuppliers[0].guid !== id
    ) {
      throw redirect({
        to: `/suppliers/${supplier.replacedBySuppliers[0].guid}${suffix}` as string,
        search: (prev) => prev,
        replace: true,
      });
    }

    // Canonicalise the URL: numeric ids and case-variant GUIDs both redirect to the exact GUID.
    if (id !== supplier.guid) {
      throw redirect({
        to: `/suppliers/${supplier.guid}${suffix}` as string,
        search: (prev) => prev,
        replace: true,
      });
    }
  },
  component: SupplierDetailLayout,
});

// /suppliers/$id/buyer/$buyerGuid (singular) — legacy alias → redirects to /suppliers/$id/buyers/$buyerGuid
export const supplierBuyerLegacyRoute = createRoute({
  getParentRoute: () => supplierIdLayoutRoute,
  path: "/buyer/$buyerGuid",
  staticData: { pageName: "" },
  component: function SupplierBuyerLegacyRedirect() {
    const { id, buyerGuid } = supplierBuyerLegacyRoute.useParams();
    return <Navigate to="/suppliers/$id/buyers/$buyerGuid" params={{ id, buyerGuid }} replace />;
  },
});

// /suppliers/$id/buyers/saved_buyers — explicit literal route (must beat /buyers/$buyerGuid)
export const supplierSavedBuyersRoute = createRoute({
  getParentRoute: () => supplierIdLayoutRoute,
  path: "/buyers/saved_buyers",
  staticData: { pageName: "Supplier Profile" },
  component: SupplierProfilePage,
});

// /suppliers/$id/buyers/$buyerGuid — supplier <> buyer relationship sub-page
export const supplierBuyerRoute = createRoute({
  getParentRoute: () => supplierIdLayoutRoute,
  path: "/buyers/$buyerGuid",
  staticData: { pageName: "Supplier buyer relationship" },
  component: SupplierBuyerRelationshipPageRoute,
});

// /suppliers/$id/$ — catch-all for tab paths (/, /awards, /mentions, /upcoming_expiries, /frameworks, /buyers)
export const supplierDetailRoute = createRoute({
  getParentRoute: () => supplierIdLayoutRoute,
  path: "/$",
  staticData: { pageName: "" },
  component: SupplierProfilePage,
});

export const suppliersRouteTree = suppliersLayoutRoute.addChildren([
  suppliersIndexRoute,
  suppliersSearchRoute,
  supplierIdLayoutRoute.addChildren([
    supplierBuyerLegacyRoute,
    supplierSavedBuyersRoute,
    supplierBuyerRoute,
    supplierDetailRoute,
  ]),
]);
